import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCartStore } from '../store/cart.store';
import { capitalizeProductName } from '../utils/capitalize';
import { getFirstProductImage } from '../utils/product-images';
import { ProductsService, ProductSearchResult } from '../services/products/products.service';
import { RegistroModal } from '../components/modals/RegistroModal';
import { WhatsAppPromoSection } from '../components/whatsapp/WhatsAppPromoSection';
import { 
  Truck,
  Phone,
  Clock,
  Pill,
  Heart,
  Baby,
  Activity,
  Sparkles,
  Droplets,
  ChevronRight,
  MapPin,
  ShoppingCart,
  Check
} from 'lucide-react';

export const InicioPage = () => {
  const { addItem } = useCartStore();
  const [addedItems, setAddedItems] = useState<number[]>([]);
  const [showRegistroModal, setShowRegistroModal] = useState(false);

  const categorias = [
    { nombre: "Medicamentos", icono: <Pill className="w-8 h-8" />, color: "bg-[#2196F3]/10 text-[#2196F3]", slug: "medicamentos" },
    { nombre: "Cuidado Personal", icono: <Sparkles className="w-8 h-8" />, color: "bg-[#4CAF50]/10 text-[#4CAF50]", slug: "cuidado-personal" },
    { nombre: "Higiene Femenina", icono: <Droplets className="w-8 h-8" />, color: "bg-[#E91E63]/10 text-[#E91E63]", slug: "higiene-femenina" },
    { nombre: "Salud Sexual", icono: <Heart className="w-8 h-8" />, color: "bg-[#F44336]/10 text-[#F44336]", slug: "salud-sexual" },
    { nombre: "Vitaminas y Suplementos", icono: <Activity className="w-8 h-8" />, color: "bg-[#FF9800]/10 text-[#FF9800]", slug: "vitaminas-y-suplementos" },
    { nombre: "Bebés", icono: <Baby className="w-8 h-8" />, color: "bg-[#2196F3]/10 text-[#1976D2]", slug: "bebes" },
  ];

  const [productosDestacados, setProductosDestacados] = useState<ProductSearchResult[]>([]);
  const [productosLoading, setProductosLoading] = useState(true);

  useEffect(() => {
    const cargarProductos = async () => {
      setProductosLoading(true);
      const response = await ProductsService.getPopular(8);
      if (response.success && response.data.length > 0) {
        setProductosDestacados(response.data);
      } else {
        // Fallback a datos estáticos si la API falla
        setProductosDestacados([]);
      }
      setProductosLoading(false);
    };
    cargarProductos();
  }, []);

  const getIconoCategoria = (categoria: string) => {
    const iconos: Record<string, string> = {
      'medicamentos': '💊',
      'cuidado-personal': '🧴',
      'higiene-femenina': '🌸',
      'salud-sexual': '💕',
      'vitaminas-y-suplementos': '🍊',
      'bebes': '👶',
      'equipos-medicos': '🏥',
    };
    return iconos[categoria] || '📦';
  };

  const handleAddToCart = (producto: ProductSearchResult) => {
    const principal = producto.presentaciones?.find((p: any) => p.esPrincipal) || producto.presentaciones?.[0];
    addItem({
      id: producto.id,
      nombre: producto.nombre,
      precio: principal?.precio || producto.precio || 0,
      precioAntes: principal?.precioAntes,
      imagen: getFirstProductImage(producto.imagenes) || getIconoCategoria(producto.categoria),
      receta: producto.requiereReceta,
      categoria: producto.categoria,
    }, true); // true = abrir carrito automáticamente

    // Mostrar feedback visual temporal
    setAddedItems(prev => [...prev, Number(producto.id)]);
    setTimeout(() => {
      setAddedItems(prev => prev.filter(id => id !== Number(producto.id)));
    }, 1500);
  };

  return (
    <div className="min-h-screen w-full transition-all duration-300 bg-gradient-to-br from-gray-50 via-white to-gray-100">
      
      {/* Promo WhatsApp permanente - PRIMERA SECCIÓN */}
      <WhatsAppPromoSection />

      {/* Categorías */}
      <section className="py-16 px-2 sm:px-3 lg:px-4 bg-white">
        <div className="w-full mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              Categorías Populares
            </h2>
            <Link 
              to="/productos" 
              className="flex items-center text-[#2196F3] hover:text-[#1976D2] font-medium"
            >
              Ver todas <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categorias.map((cat, index) => (
              <Link
                key={index}
                to={`/productos?categoria=${cat.slug}`}
                className="group p-6 rounded-2xl transition-all duration-300 hover:scale-105 bg-gray-50 hover:bg-white hover:shadow-lg"
              >
                <div className={`w-16 h-16 rounded-2xl ${cat.color} flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform`}>
                  {cat.icono}
                </div>
                <p className="text-center font-medium text-gray-900">
                  {cat.nombre}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Productos Destacados */}
      <section className="py-16 px-2 sm:px-3 lg:px-4">
        <div className="w-full mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              Productos Destacados
            </h2>
            <Link 
              to="/productos" 
              className="flex items-center text-[#2196F3] hover:text-[#1976D2] font-medium"
            >
              Ver todos <ChevronRight className="w-5 h-5" />
            </Link>
          </div>

          {productosLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6 items-stretch">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="rounded-2xl overflow-hidden bg-white shadow-lg animate-pulse h-full flex flex-col">
                  <div className="aspect-square bg-gray-200"></div>
                  <div className="p-4 space-y-2 flex-1 flex flex-col">
                    <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-8 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-8 bg-gray-200 rounded w-full mt-auto"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : productosDestacados.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No hay productos destacados disponibles en este momento.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6 items-stretch">
              {productosDestacados.map((producto) => {
                const principal = producto.presentaciones?.find((p: any) => p.esPrincipal) || producto.presentaciones?.[0];
                const imgUrl = getFirstProductImage(producto.imagenes);
                const precio = principal?.precio || producto.precio || 0;
                const precioAntes = principal?.precioAntes;
                const descuento = precioAntes ? Math.round((1 - precio / precioAntes) * 100) : 0;
                return (
                  <div 
                    key={producto.id}
                    className="group rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02] bg-white shadow-lg hover:shadow-xl h-full flex flex-col"
                  >
                    <div className="block">
                      <div className="relative p-4 lg:p-6">
                        {!!precioAntes && (
                          <span className="absolute top-2 left-2 lg:top-4 lg:left-4 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 lg:px-2 lg:py-1 rounded z-10">
                            -{descuento}%
                          </span>
                        )}
                        <div className="aspect-square flex items-center justify-center overflow-hidden rounded-xl bg-gray-50">
                          {imgUrl ? (
                            <img 
                              src={imgUrl} 
                              alt={producto.nombre}
                              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                              loading="lazy"
                            />
                          ) : (
                            <span className="text-5xl sm:text-6xl">{getIconoCategoria(producto.categoria)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="p-3 lg:p-4 pt-0 flex flex-col flex-1">
                      <span className="inline-block text-[10px] uppercase tracking-wider font-semibold text-[#4CAF50] bg-[#4CAF50]/10 px-2 py-0.5 rounded-full mb-2">
                        {producto.categoria.replace(/-/g, ' ')}
                      </span>
                      <h3 className="font-semibold mb-2 text-sm lg:text-base text-gray-900 line-clamp-2 min-h-[2.5rem] lg:min-h-[3rem]">
                        {capitalizeProductName(producto.nombre)}
                      </h3>
                      <div className="flex items-center space-x-2 mb-3 lg:mb-4">
                        <span className="text-[#2196F3] font-bold text-sm lg:text-base">
                          ${precio.toLocaleString()}
                        </span>
                        {!!precioAntes && (
                          <span className="text-xs lg:text-sm line-through text-gray-400">
                            ${precioAntes.toLocaleString()}
                          </span>
                        )}
                      </div>
                      <button 
                        onClick={() => handleAddToCart(producto)}
                        disabled={addedItems.includes(Number(producto.id))}
                        className={`w-full py-2 lg:py-2.5 rounded-lg font-medium transition-all duration-300 flex items-center justify-center space-x-2 text-sm mt-auto ${
                          addedItems.includes(Number(producto.id))
                            ? 'bg-[#4CAF50] text-white'
                            : 'bg-gradient-to-r from-[#4CAF50] to-[#2196F3] hover:opacity-90 text-white'
                        }`}
                      >
                        {addedItems.includes(Number(producto.id)) ? (
                          <>
                            <Check className="w-4 h-4" />
                            <span>Agregado</span>
                          </>
                        ) : (
                          <>
                            <ShoppingCart className="w-4 h-4" />
                            <span>Agregar</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Banner Registro - Sección Principal */}
      <section className="py-16 px-2 sm:px-3 lg:px-4 relative overflow-hidden">
        {/* Background decorativo */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#4CAF50] via-[#2196F3] to-[#4CAF50]"></div>
        <div className="absolute inset-0 bg-black/10"></div>
        
        {/* Elementos flotantes decorativos */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-white/5 rounded-full blur-2xl animate-bounce"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/20 rounded-full blur-lg animate-pulse delay-300"></div>
        
        <div className="relative max-w-4xl mx-auto text-center">
          {/* Badge superior */}
          <div className="inline-flex items-center px-6 py-3 bg-white/20 backdrop-blur rounded-full mb-6">
            <div className="w-3 h-3 bg-yellow-400 rounded-full mr-3 animate-pulse"></div>
            <span className="text-white font-semibold text-sm">¡OFERTA ESPECIAL DE LANZAMIENTO!</span>
          </div>

          {/* Título principal */}
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            ¡Regístrate GRATIS y obtén 
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-300">
              15% de descuento
            </span>
            en tu primer pedido!
          </h2>

          {/* Descripción */}
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
            Miles de productos disponibles, entrega a domicilio gratis en compras superiores a $50.000 
            y atención farmacéutica profesional
          </p>

          {/* Beneficios */}
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="flex items-center justify-center space-x-3 text-white">
              <div className="w-8 h-8 bg-[#4CAF50] rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold">Descuentos exclusivos</span>
            </div>
            <div className="flex items-center justify-center space-x-3 text-white">
              <div className="w-8 h-8 bg-[#4CAF50] rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold">Seguimiento en tiempo real</span>
            </div>
            <div className="flex items-center justify-center space-x-3 text-white">
              <div className="w-8 h-8 bg-[#4CAF50] rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold">Atención prioritaria</span>
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button 
              onClick={() => setShowRegistroModal(true)}
              className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-[#0d5c3b] transition-all duration-300 bg-white rounded-2xl hover:bg-gray-100 hover:scale-105 hover:shadow-2xl"
            >
              <span className="flex items-center space-x-3">
                <span>Crear Mi Cuenta GRATIS</span>
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
            <a 
              href="/productos"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white/30 rounded-2xl hover:bg-white/10 hover:border-white/50 transition-all duration-300"
            >
              Ver Productos
            </a>
          </div>

          {/* Contador de usuarios */}
          <div className="mt-10 flex items-center justify-center space-x-2 text-white/80">
            <div className="flex -space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full border-2 border-white"></div>
              <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-teal-500 rounded-full border-2 border-white"></div>
              <div className="w-8 h-8 bg-gradient-to-br from-[#4CAF50] to-[#2196F3] rounded-full border-2 border-white"></div>
              <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold text-white">+</div>
            </div>
            <span className="text-sm">Más de 5,000 clientes ya se registraron este mes</span>
          </div>
        </div>
      </section>

      {/* Servicios */}
      <section className="py-16 px-2 sm:px-3 lg:px-4 bg-gradient-to-br from-[#4CAF50]/5 to-[#2196F3]/5">
        <div className="w-full mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 text-gray-900">
            Nuestros Servicios
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 rounded-2xl text-center bg-white shadow-lg">
              <div className="w-16 h-16 bg-[#4CAF50]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="w-8 h-8 text-[#4CAF50]" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Domicilios</h3>
              <p className="text-gray-600">
                Entrega rápida de tus medicamentos a la puerta de tu casa. 
                Gratis en compras superiores a $50.000.
              </p>
            </div>

            <div className="p-8 rounded-2xl text-center bg-white shadow-lg">
              <div className="w-16 h-16 bg-[#2196F3]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-[#2196F3]" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Asesoría Telefónica</h3>
              <p className="text-gray-600">
                Farmacéuticos profesionales disponibles para responder 
                tus preguntas y orientarte.
              </p>
            </div>

            <div className="p-8 rounded-2xl text-center bg-white shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-[#4CAF50]/10 to-[#2196F3]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-[#2196F3]" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Tienda Física</h3>
              <p className="text-gray-600">
                Visítanos en nuestra sede principal. Horario extendido 
                de lunes a domingo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Promo WhatsApp permanente */}
      <WhatsAppPromoSection />

      {/* Modal de Registro */}
      <RegistroModal 
        isOpen={showRegistroModal}
        onClose={() => setShowRegistroModal(false)}
        onSuccess={() => {
          // Aquí podrías mostrar un mensaje adicional o redirigir
        }}
      />
    </div>
  );
};

export default InicioPage;
